import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { getHeroText, updateHeroText, resetHeroText } from '../../services/heroTextService';

function HeroTextManagement() {
  const { admin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [heroText, setHeroText] = useState({
    titleLine1: '',
    titleHighlight: '',
    titleLine2: '',
    description: ''
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadHeroText();
  }, []);

  const loadHeroText = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getHeroText();
      setHeroText({
        titleLine1: data.titleLine1 || '',
        titleHighlight: data.titleHighlight || '',
        titleLine2: data.titleLine2 || '',
        description: data.description || ''
      });
      if (data.updatedAt) {
        setLastUpdated(data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt));
      }
    } catch (err) {
      console.error('Error loading hero text:', err);
      setError('Failed to load hero text settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setHeroText(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!heroText.titleLine1 || !heroText.titleHighlight || !heroText.titleLine2 || !heroText.description) {
      setError('All fields are required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const result = await updateHeroText(
        heroText,
        admin.uid,
        admin.email
      );

      if (result.success) {
        setSuccess('Hero text updated successfully!');
        await loadHeroText(); // Reload to get updated timestamp
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating hero text:', err);
      setError(err.message || 'Failed to update hero text');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset hero text to default values?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const result = await resetHeroText(admin.uid, admin.email);

      if (result.success) {
        setSuccess('Hero text reset to defaults!');
        await loadHeroText();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error resetting hero text:', err);
      setError(err.message || 'Failed to reset hero text');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                <i className="bi bi-textarea-t me-2"></i>
                Hero Text Management
              </h4>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {success}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccess('')}
                  ></button>
                </div>
              )}

              {lastUpdated && (
                <div className="alert alert-info">
                  <i className="bi bi-clock-history me-2"></i>
                  <strong>Last Updated:</strong> {lastUpdated.toLocaleString()}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12 mb-4">
                    <h5 className="mb-3">Hero Title</h5>
                    <p className="text-muted small mb-3">
                      The hero title appears as: <strong>"{heroText.titleLine1} {heroText.titleHighlight} {heroText.titleLine2}"</strong>
                    </p>
                    
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label htmlFor="titleLine1" className="form-label">
                          First Part <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="titleLine1"
                          value={heroText.titleLine1}
                          onChange={(e) => handleInputChange('titleLine1', e.target.value)}
                          placeholder="Where"
                          required
                        />
                        <small className="text-muted">Text before the highlighted word</small>
                      </div>

                      <div className="col-md-4 mb-3">
                        <label htmlFor="titleHighlight" className="form-label">
                          Highlighted Word <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="titleHighlight"
                          value={heroText.titleHighlight}
                          onChange={(e) => handleInputChange('titleHighlight', e.target.value)}
                          placeholder="TIME"
                          required
                        />
                        <small className="text-muted">The word that will be highlighted with gradient</small>
                      </div>

                      <div className="col-md-4 mb-3">
                        <label htmlFor="titleLine2" className="form-label">
                          Last Part <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="titleLine2"
                          value={heroText.titleLine2}
                          onChange={(e) => handleInputChange('titleLine2', e.target.value)}
                          placeholder="pays"
                          required
                        />
                        <small className="text-muted">Text after the highlighted word</small>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12 mb-4">
                    <h5 className="mb-3">Hero Description</h5>
                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">
                        Description Text <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="description"
                        rows="3"
                        value={heroText.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="We don't wait for success, we accelerate it."
                        required
                      />
                      <small className="text-muted">The tagline that appears below the hero title</small>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Reset to Defaults
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="mb-2">Preview:</h6>
                <div className="p-3 bg-white rounded border">
                  <h3 className="mb-2">
                    {heroText.titleLine1 || 'Where'}{' '}
                    <span className="text-primary fw-bold">{heroText.titleHighlight || 'TIME'}</span>
                    {' '}{heroText.titleLine2 || 'pays'}
                  </h3>
                  <p className="text-muted mb-0">{heroText.description || "We don't wait for success, we accelerate it."}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroTextManagement;

